import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Calculator, ShoppingCart, AlertTriangle, Info } from "lucide-react";

const fetcher = (u) => api.get(u).then(r=>r.data);
const num = (v) => (Math.round((v||0) * 100) / 100).toLocaleString();

export default function RMPlanningPage() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [source, setSource] = useState("both");
  const [wastage, setWastage] = useState(3);
  const [bagSize, setBagSize] = useState(25);
  const [selected, setSelected] = useState({});
  const [creating, setCreating] = useState(false);

  const qs = `/mrp/plan?source=${source}&wastage_pct=${wastage}&bag_size=${bagSize}` +
    (fromDate ? `&from_date=${fromDate}` : "") + (toDate ? `&to_date=${toDate}` : "");
  const { data: plan, isLoading } = useSWR(qs, fetcher);
  const rows = plan?.rows || [];

  const selectable = rows.filter(r => r.net_shortage_kg > 0 && !r.unmapped);
  const selCount = Object.values(selected).filter(Boolean).length;

  const toggle = (mid) => setSelected(s => ({ ...s, [mid]: !s[mid] }));
  const toggleAll = () => {
    const allOn = selectable.every(r => selected[r.material_id]);
    const next = { ...selected };
    selectable.forEach(r => { next[r.material_id] = !allOn; });
    setSelected(next);
  };

  const createPOs = async () => {
    const items = rows.filter(r => selected[r.material_id] && r.net_shortage_kg > 0)
      .map(r => ({ material_id: r.material_id, qty: r.suggested_order_kg }));
    if (!items.length) { toast.info("Select at least one shortage line"); return; }
    setCreating(true);
    try {
      const r = await api.post("/mrp/create-draft-pos", { items });
      const n = r.data.created.length;
      if (n > 0) toast.success(`Created ${n} draft PO(s): ${r.data.created.map(c=>c.ppo_no).join(", ")}`);
      (r.data.skipped||[]).forEach(s => toast.warning(`${s.name || s.material_id}: ${s.reason}`));
      setSelected({});
      mutate("/purchase-orders");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create POs"); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-4" data-testid="rm-planning-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Calculator size={22} className="text-sky-600"/> RM Planning (MRP)</h1>
          <p className="text-sm text-slate-500">Raw material requirement from open sales & production orders, netted against stock and open POs.</p>
        </div>
        <button data-testid="btn-create-draft-pos" onClick={createPOs} disabled={creating || selCount===0}
          className="bg-sky-600 hover:bg-sky-700 text-white text-sm px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50">
          <ShoppingCart size={14}/> {creating ? "Creating..." : `Create draft PO${selCount ? ` (${selCount})` : ""}`}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="mrp-filters">
        <div>
          <label className="text-[11px] uppercase text-slate-500 font-semibold">From date</label>
          <input data-testid="mrp-from-date" type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} className="block text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
        </div>
        <div>
          <label className="text-[11px] uppercase text-slate-500 font-semibold">To date</label>
          <input data-testid="mrp-to-date" type="date" value={toDate} onChange={e=>setToDate(e.target.value)} className="block text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
        </div>
        <div>
          <label className="text-[11px] uppercase text-slate-500 font-semibold">Demand source</label>
          <select data-testid="mrp-source" value={source} onChange={e=>setSource(e.target.value)} className="block text-sm border border-slate-200 rounded-md px-2 py-1.5">
            <option value="both">All open SOs + Production Orders</option>
            <option value="open_so">Open Sales Orders only</option>
            <option value="open_po">Open Production Orders only</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase text-slate-500 font-semibold">Process wastage %</label>
          <input data-testid="mrp-wastage" type="number" min="0" step="0.5" value={wastage} onChange={e=>setWastage(Number(e.target.value))} className="block w-24 text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
        </div>
        <div>
          <label className="text-[11px] uppercase text-slate-500 font-semibold">Default bag size (kg)</label>
          <input data-testid="mrp-bag-size" type="number" min="1" value={bagSize} onChange={e=>setBagSize(Number(e.target.value))} className="block w-24 text-sm border border-slate-200 rounded-md px-2 py-1.5"/>
        </div>
        {plan && (
          <div className="text-[11px] text-slate-500 ml-auto" data-testid="mrp-lines-included">
            Demand lines: {plan.lines_included?.sales_orders||0} SOs · {plan.lines_included?.production_orders||0} POs
          </div>
        )}
      </div>

      <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 text-[12px] text-slate-700 flex gap-2">
        <Info size={14} className="text-sky-600 shrink-0 mt-0.5"/>
        <span>Required kg = qty × (container + cap + inner weight g) ÷ 1000 × (1 + wastage%) → split into masterbatch by MB%, base polymer reduced by regrind%. Net shortage = required − stock − open PO. Suggested order rounds shortage up to bag size.</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-auto max-h-[65vh]">
          <table className="data-table" data-testid="mrp-table">
            <thead>
              <tr>
                <th><input type="checkbox" data-testid="mrp-select-all" onChange={toggleAll} checked={selectable.length>0 && selectable.every(r=>selected[r.material_id])}/></th>
                <th>Material</th><th>Type</th><th>Grade</th>
                <th className="text-right">Required (kg)</th><th className="text-right">Stock (kg)</th>
                <th className="text-right">Open PO (kg)</th><th className="text-right">Net Shortage (kg)</th>
                <th className="text-right">Suggested Order (kg)</th><th>Preferred Supplier</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="text-center text-slate-400 py-6">Computing…</td></tr>}
              {!isLoading && rows.length===0 && <tr><td colSpan={10} className="text-center text-slate-400 py-6">No demand found for the selected filters.</td></tr>}
              {rows.map(r => (
                <tr key={r.material_id || r.material_type} data-testid={`mrp-row-${r.material_id || r.material_type}`} className={r.net_shortage_kg>0 ? "bg-red-50/40" : ""}>
                  <td>{r.net_shortage_kg>0 && !r.unmapped && <input type="checkbox" data-testid={`mrp-select-${r.material_id}`} checked={!!selected[r.material_id]} onChange={()=>toggle(r.material_id)}/>}</td>
                  <td>
                    <div className="font-medium text-slate-900">{r.name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{r.code}</div>
                    {r.unmapped && <div className="text-[10px] text-amber-700 flex items-center gap-1"><AlertTriangle size={10}/> Create/link an RM master of this type</div>}
                  </td>
                  <td><span className="pill pill-idle">{r.material_type}</span></td>
                  <td className="text-xs">{r.grade || "—"}</td>
                  <td className="text-right font-mono">{num(r.required_kg)}</td>
                  <td className={`text-right font-mono ${r.below_reorder ? "text-amber-700 font-semibold" : ""}`}>{num(r.stock_kg)}</td>
                  <td className="text-right font-mono">{num(r.open_po_kg)}</td>
                  <td className={`text-right font-mono font-semibold ${r.net_shortage_kg>0 ? "text-red-700" : "text-emerald-700"}`}>{num(r.net_shortage_kg)}</td>
                  <td className="text-right font-mono">{r.suggested_order_kg ? num(r.suggested_order_kg) : "—"}</td>
                  <td className="text-xs">{r.preferred_supplier_name || <span className="text-slate-400">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-slate-500">
        Draft POs land in <Link to="/purchase-orders-supplier" className="text-sky-600 underline">Purchase Orders</Link> with status DRAFT, grouped by preferred supplier.
      </div>
    </div>
  );
}
